import {
  Product,
  ProductHighlight,
  PromoGroupHighlights,
} from '@diamond/shared/types';
import {
  getActivePromo,
  getItemCardTrackingParams,
  trackEvent,
} from '@diamond/shared/utils';
import { useEffect, useRef } from 'react';

import { TRACKING_EVENTS, TRACKING_PAGES } from './tracking-events';

// Type helper untuk individual product highlight
type ProductHighlightItem = ProductHighlight[number];

/**
 * Helper function untuk mendapatkan page name berdasarkan router path
 * Digunakan untuk tracking events yang membutuhkan context halaman
 */
export const getCurrentPageName = (pathname: string): string => {
  switch (pathname) {
    case '/':
      return TRACKING_PAGES.BERANDA;
    case '/product':
      return TRACKING_PAGES.PRODUCT_LIST;
    case '/cart':
      return TRACKING_PAGES.CART;
    case '/profile':
      return TRACKING_PAGES.ACCOUNT_DETAIL;
    case '/notification':
      return TRACKING_PAGES.NOTIFICATION;
    case '/recipe':
      return TRACKING_PAGES.RECIPE;
    case '/wishlist':
      return TRACKING_PAGES.WISHLIST;
    case '/shopping-list':
      return TRACKING_PAGES.SHOPPING_LIST;
    case '/checkout-new':
      return TRACKING_PAGES.CHECKOUT;
    case '/promo':
      return TRACKING_PAGES.PROMO_LIST;
    case '/login':
      return TRACKING_PAGES.LOGIN;
    case '/register':
      return TRACKING_PAGES.REGISTER;
    case '/my-order':
      return TRACKING_PAGES.MY_ORDER;
    default:
      // For dynamic routes like /product/[id], extract the base path
      if (pathname.startsWith('/product/')) {
        return TRACKING_PAGES.PRODUCT_DETAIL;
      }
      if (pathname.startsWith('/order-detail/')) {
        return TRACKING_PAGES.ORDER_DETAIL;
      }
      if (pathname.startsWith('/promo/')) {
        return TRACKING_PAGES.PROMO_DETAIL;
      }
      if (pathname.startsWith('/recipe/')) {
        return TRACKING_PAGES.RECIPE_DETAIL;
      }
      if (pathname.startsWith('/shopping-list/')) {
        return TRACKING_PAGES.SHOPPING_LIST_DETAIL;
      }
      // Fallback to clean path name for unknown routes
      return pathname.replace('/', '').replace('-', ' ') || 'Unknown Page';
  }
};

/**
 * Custom hook untuk tracking VIEW_ITEM_LIST di product list
 * Track saat product list berhasil dimuat
 */
export const useProductListTracking = (
  products: Product[] | undefined,
  filters: {
    category_lv1?: string;
    search_query?: string;
  },
  enabled = true,
  options?: {
    sectionName?: string;
    pageName?: string;
    sectionId?: string;
  }
) => {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (products && products.length > 0 && !hasTracked.current && enabled) {
      const categoryName = filters.category_lv1 || 'All Categories';
      const searchKeyword = filters.search_query || '';
      const sectionName = options?.sectionName;
      const pageName = options?.pageName || TRACKING_PAGES.PRODUCT_LIST;
      const sectionId = options?.sectionId;

      // Use custom section name if provided, otherwise use default logic
      const listName = sectionName
        ? sectionName
        : searchKeyword
        ? 'Search Result'
        : `Category | ${categoryName}`;

      const listId = sectionName
        ? sectionName
        : searchKeyword
        ? `Search | ${searchKeyword}`
        : `Category | ${filters.category_lv1 || 'all'}`;

      trackEvent(TRACKING_EVENTS.VIEW_ITEM_LIST, {
        items: products.map((product, i: number) => ({
          index: i,
          ...getItemCardTrackingParams(
            product,
            1,
            listName,
            pageName,
            sectionId
          ),
        })),
        currency: 'IDR',
        item_list_name: listName,
        item_list_id: listId,
        section_name: sectionName,
      });
      hasTracked.current = true;
    }
  }, [products, filters, enabled, options]);
};

/**
 * Custom hook untuk tracking VIEW_PROMOTION di product list
 * Track produk yang memiliki promo aktif
 */
export const useProductPromotionTracking = (
  products: Product[] | undefined,
  enabled = true,
  section = '-'
) => {
  const hasTrackedViewPromotion = useRef(false);

  useEffect(() => {
    if (
      products &&
      products.length > 0 &&
      !hasTrackedViewPromotion.current &&
      enabled
    ) {
      // Group products by their active promo code/mechanism
      type ProductWithPromoData = {
        product: Product;
        index: number;
        activePromoData: ReturnType<typeof getActivePromo>;
      };
      const productsByPromo: Record<string, ProductWithPromoData[]> = {};

      products.forEach((product, i) => {
        const activePromoData = getActivePromo(product, 1);

        // Only include products that have active promos
        if (activePromoData) {
          const promoKey = `${activePromoData.promo.mechanism}`;

          if (!productsByPromo[promoKey]) {
            productsByPromo[promoKey] = [];
          }

          productsByPromo[promoKey].push({
            product,
            index: i,
            activePromoData,
          });
        }
      });

      // Send view_promotion events untuk setiap grup promo yang berbeda
      if (Object.keys(productsByPromo).length > 0) {
        Object.entries(productsByPromo).forEach(([, productGroup]) => {
          if (productGroup.length === 0) return;

          const firstProduct = productGroup[0];
          const activePromoData = firstProduct.activePromoData;

          if (!activePromoData) return;

          const promotionId = `${activePromoData.promo.mechanism} | ${firstProduct.product.item_code}`;
          const promotionName = activePromoData.promo.mechanism;

          trackEvent(TRACKING_EVENTS.VIEW_PROMOTION, {
            creative_name: promotionName,
            creative_slot: promotionId,
            promotion_id: promotionId,
            promotion_name: promotionName,
            items: productGroup.map(({ product, index, activePromoData }) => {
              const price =
                product?.prices?.length > 0 ? product.prices[0].price : 0;

              // Calculate discount
              let discountAmount = 0;
              if (activePromoData) {
                const { promo, type } = activePromoData;

                if (type === 'bundled_discount' || type === 'discount') {
                  const finalPrice =
                    price -
                    ('price_after_discount' in promo &&
                    promo.price_after_discount !== null
                      ? price - promo.price_after_discount
                      : 0);

                  discountAmount = price - finalPrice;
                }
              }

              const couponCode = activePromoData?.promo.promo_code;

              return {
                item_id: product.item_code?.toString() || '',
                item_name: product.title || '',
                affiliation: 'Sukanda Onelink',
                coupon: couponCode,
                discount: discountAmount,
                index: index,
                item_brand: product.brand_name || '',
                item_category: product.category_name || '',
                item_category2: product.sub_category_name || '',
                item_category3: '',
                item_category4: '',
                item_category5: '',
                item_list_id: section,
                item_list_name: section,
                item_variant: '',
                location_id: '',
                price: price,
                quantity: 1,
              };
            }),
            item_list_id: section,
            item_list_name: section,
            section_name: section,
          });
        });
      }

      hasTrackedViewPromotion.current = true;
    }
  }, [products, enabled]);
};

/**
 * Custom hook untuk tracking VIEW_ITEM_LIST di promo group highlights
 * Track saat promo products berhasil dimuat
 */
export const usePromoGroupTracking = (
  promoGroupHighlights: PromoGroupHighlights | undefined,
  pageName: string = TRACKING_PAGES.BERANDA
) => {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (
      promoGroupHighlights?.products &&
      promoGroupHighlights.products.length > 0 &&
      !hasTracked.current
    ) {
      trackEvent(TRACKING_EVENTS.VIEW_ITEM_LIST, {
        items: promoGroupHighlights.products.map((product, i: number) => ({
          index: i,
          ...getItemCardTrackingParams(product, 1, 'Promo Highlight', pageName),
        })),
        currency: 'IDR',
        item_list_name: 'Promo Highlight',
        item_list_id: `Promo Highlight | ${
          promoGroupHighlights?.promo?.mechanism || 'Unknown'
        }`,
      });
      hasTracked.current = true;
    }
  }, [
    promoGroupHighlights?.products,
    promoGroupHighlights?.promo?.mechanism,
    pageName,
  ]);
};

/**
 * Custom hook untuk tracking VIEW_PROMOTION di promo group highlights
 * Track promo group dengan active promotion
 */
export const usePromoGroupPromotionTracking = (
  promoGroupHighlights: PromoGroupHighlights | undefined
) => {
  const hasTrackedViewPromotion = useRef(false);

  useEffect(() => {
    if (
      promoGroupHighlights?.products &&
      promoGroupHighlights.products.length > 0 &&
      !hasTrackedViewPromotion.current
    ) {
      // Similar logic as product list but for promo group
      type ProductWithPromoData = {
        product: NonNullable<typeof promoGroupHighlights.products>[0];
        index: number;
        activePromoData: ReturnType<typeof getActivePromo>;
      };
      const productsByPromo: Record<string, ProductWithPromoData[]> = {};

      promoGroupHighlights.products.forEach((product, i: number) => {
        const activePromoData = getActivePromo(product, 1);

        if (activePromoData) {
          const promoKey = `${activePromoData.promo.mechanism}`;

          if (!productsByPromo[promoKey]) {
            productsByPromo[promoKey] = [];
          }

          productsByPromo[promoKey].push({
            product,
            index: i,
            activePromoData,
          });
        }
      });

      if (Object.keys(productsByPromo).length > 0) {
        Object.entries(productsByPromo).forEach(([, productGroup]) => {
          if (productGroup.length === 0) return;

          const firstProduct = productGroup[0];
          const activePromoData = firstProduct.activePromoData;

          if (!activePromoData) return;

          const promotionId = `${activePromoData.promo.mechanism} | ${firstProduct.product.item_code}`;
          const promotionName = activePromoData.promo.mechanism;

          trackEvent(TRACKING_EVENTS.VIEW_PROMOTION, {
            creative_name: promotionName,
            creative_slot: promotionId,
            promotion_id: promotionId,
            promotion_name: promotionName,
            items: productGroup.map(({ product, index, activePromoData }) => {
              const price =
                product?.prices?.length > 0 ? product.prices[0].price : 0;

              let discountAmount = 0;
              if (activePromoData) {
                const { promo, type } = activePromoData;

                if (type === 'bundled_discount' || type === 'discount') {
                  const finalPrice =
                    price -
                    ('price_after_discount' in promo &&
                    promo.price_after_discount !== null
                      ? price - promo.price_after_discount
                      : 0);

                  discountAmount = price - finalPrice;
                }
              }

              const couponCode = activePromoData?.promo.promo_code;

              return {
                item_id: product.item_code?.toString() || '',
                item_name: product.title || '',
                affiliation: 'Sukanda Onelink',
                coupon: couponCode,
                discount: discountAmount,
                index: index,
                item_brand: product.brand_name || '',
                item_category: product.category_name || '',
                item_category2: product.sub_category_name || '',
                item_category3: '',
                item_category4: '',
                item_category5: '',
                item_list_id: 'Promo Highlight',
                item_list_name: 'Promo Highlight',
                item_variant: '',
                location_id: '',
                price: price,
                quantity: 1,
              };
            }),
          });
        });
      }

      hasTrackedViewPromotion.current = true;
    }
  }, [promoGroupHighlights]);
};

/**
 * Custom hook untuk tracking VIEW_ITEM_LIST di product highlights
 * Track setiap product highlight section
 */
export const useProductHighlightTracking = (
  productHighlights: ProductHighlight | undefined,
  pageName: string = TRACKING_PAGES.BERANDA
) => {
  const hasTrackedProductHighlights = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (productHighlights && productHighlights.length > 0) {
      productHighlights.forEach((highlightData: ProductHighlightItem) => {
        const highlightId = highlightData.id;

        // Only track each highlight once
        if (
          !hasTrackedProductHighlights.current.has(highlightId) &&
          highlightData.products &&
          highlightData.products.length > 0
        ) {
          // Track VIEW_ITEM_LIST for this highlight
          trackEvent(TRACKING_EVENTS.VIEW_ITEM_LIST, {
            items: highlightData.products.map((product: Product, i: number) => {
              return {
                index: i,
                ...getItemCardTrackingParams(
                  product,
                  1,
                  `Product Highlight | ${highlightData.title}`,
                  pageName
                ),
              };
            }),
            currency: 'IDR',
            item_list_name: `Product Highlight | ${highlightData.title}`,
            item_list_id: `Product Highlight | ${highlightData.id}`,
          });

          hasTrackedProductHighlights.current.add(highlightId);
        }
      });
    }
  }, [productHighlights, pageName]);
};

/**
 * Custom hook untuk tracking VIEW_PROMOTION di product highlights
 * Track product highlights yang memiliki active promotion
 */
export const useProductHighlightPromotionTracking = (
  productHighlights: ProductHighlight | undefined
) => {
  const hasTrackedHighlightPromotion = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (productHighlights && productHighlights.length > 0) {
      productHighlights.forEach((highlightData: ProductHighlightItem) => {
        const highlightId = highlightData.id;

        // Only track each highlight promotion once
        if (
          !hasTrackedHighlightPromotion.current.has(highlightId) &&
          highlightData.products &&
          highlightData.products.length > 0
        ) {
          // Track VIEW_PROMOTION for products with active promos only
          const productsWithPromo = highlightData.products
            .map((product: Product, index: number) => {
              const activePromoData = getActivePromo(product, 1);
              return activePromoData
                ? { product, index, activePromoData }
                : null;
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);

          if (productsWithPromo.length > 0) {
            // Group by promo mechanism
            const productsByPromo: Record<string, typeof productsWithPromo> =
              {};

            productsWithPromo.forEach((item) => {
              const mechanism = item.activePromoData.promo.mechanism;
              const promoKey = mechanism ? mechanism : 'unknown';
              if (!productsByPromo[promoKey]) {
                productsByPromo[promoKey] = [];
              }
              productsByPromo[promoKey].push(item);
            });

            // Send separate view_promotion events for each promo group
            Object.entries(productsByPromo).forEach(
              ([mechanism, productGroup]) => {
                if (productGroup.length === 0) return;

                const firstProduct = productGroup[0];
                const promotionId = `${mechanism} | ${firstProduct.product.item_code}`;

                trackEvent(TRACKING_EVENTS.VIEW_PROMOTION, {
                  creative_name: mechanism,
                  creative_slot: promotionId,
                  promotion_id: promotionId,
                  promotion_name: mechanism,
                  items: productGroup.map(
                    ({
                      product,
                      index,
                      activePromoData,
                    }: {
                      product: Product;
                      index: number;
                      activePromoData: ReturnType<typeof getActivePromo>;
                    }) => {
                      const price =
                        product?.prices?.length > 0
                          ? product.prices[0].price
                          : 0;

                      let discountAmount = 0;
                      if (activePromoData) {
                        const { promo, type } = activePromoData;

                        if (
                          type === 'bundled_discount' ||
                          type === 'discount'
                        ) {
                          const finalPrice =
                            price -
                            ('price_after_discount' in promo &&
                            promo.price_after_discount !== null
                              ? price - promo.price_after_discount
                              : 0);

                          discountAmount = price - finalPrice;
                        }
                      }

                      const couponCode = activePromoData?.promo.promo_code;

                      return {
                        item_id: product.item_code?.toString() || '',
                        item_name: product.title || '',
                        affiliation: 'Sukanda Onelink',
                        coupon: couponCode,
                        discount: discountAmount,
                        index: index,
                        item_brand: product.brand_name || '',
                        item_category: product.category_name || '',
                        item_category2: product.sub_category_name || '',
                        item_category3: '',
                        item_category4: '',
                        item_category5: '',
                        item_list_id: `Product Highlight | ${highlightData.title}`,
                        item_list_name: `Product Highlight | ${highlightData.title}`,
                        item_variant: '',
                        location_id: '',
                        price: price,
                        quantity: 1,
                      };
                    }
                  ),
                });
              }
            );
          }

          hasTrackedHighlightPromotion.current.add(highlightId);
        }
      });
    }
  }, [productHighlights]);
};
